import { supabase } from './supabase'

/**
 * Create a data subject request (GDPR)
 */
export const createDataSubjectRequest = async (organizationId, clientId, requestType, details = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    const { data, error } = await supabase
      .from('data_subject_requests')
      .insert({
        organization_id: organizationId,
        client_id: clientId,
        request_type: requestType,
        requested_by: user?.id,
        request_details: details,
      })
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error creating data subject request:', error)
    throw error
  }
}

/**
 * Get all data subject requests for organization
 */
export const getDataSubjectRequests = async (organizationId, filters = {}) => {
  try {
    let query = supabase
      .from('data_subject_requests')
      .select('*, client:client_id(first_name, last_name), requested_by_user:requested_by(email), processed_by_user:processed_by(email)')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })

    if (filters.status) {
      query = query.eq('status', filters.status)
    }
    if (filters.requestType) {
      query = query.eq('request_type', filters.requestType)
    }
    if (filters.clientId) {
      query = query.eq('client_id', filters.clientId)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching data subject requests:', error)
    throw error
  }
}

/**
 * Process a data subject request
 */
export const processDataSubjectRequest = async (requestId, organizationId, status, responseData = null) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    const updates = {
      status,
      processed_by: user?.id,
      updated_at: new Date().toISOString(),
    }

    if (status === 'completed') {
      updates.completed_at = new Date().toISOString()
      if (responseData) {
        updates.response_data = responseData
      }
    }

    const { data, error } = await supabase
      .from('data_subject_requests')
      .update(updates)
      .eq('id', requestId)
      .eq('organization_id', organizationId)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error processing data subject request:', error)
    throw error
  }
}

/**
 * Export client data (GDPR Right to Data Portability)
 */
export const exportClientData = async (organizationId, clientId) => {
  try {
    // Fetch all client data
    const [
      { data: client },
      { data: medicalHistories },
      { data: examinations },
      { data: carePlans },
      { data: therapies },
      { data: sessions },
      { data: communications },
      { data: documents },
    ] = await Promise.all([
      supabase.from('clients').select('*').eq('id', clientId).eq('organization_id', organizationId).single(),
      supabase.from('medical_histories').select('*').eq('client_id', clientId).eq('organization_id', organizationId),
      supabase.from('examinations').select('*').eq('client_id', clientId).eq('organization_id', organizationId),
      supabase.from('care_plans').select('*').eq('client_id', clientId).eq('organization_id', organizationId),
      supabase.from('therapies').select('*').eq('client_id', clientId).eq('organization_id', organizationId),
      supabase.from('sessions').select('*').eq('client_id', clientId).eq('organization_id', organizationId),
      supabase.from('client_communications').select('*').eq('client_id', clientId).eq('organization_id', organizationId),
      supabase.from('client_documents').select('*').eq('client_id', clientId).eq('organization_id', organizationId),
    ])

    return {
      client,
      medical_histories: medicalHistories || [],
      examinations: examinations || [],
      care_plans: carePlans || [],
      therapies: therapies || [],
      sessions: sessions || [],
      communications: communications || [],
      documents: documents || [],
      exported_at: new Date().toISOString(),
    }
  } catch (error) {
    console.error('Error exporting client data:', error)
    throw error
  }
}

/**
 * Delete client data (GDPR Right to Erasure)
 */
export const deleteClientData = async (organizationId, clientId, reason) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    // Log the deletion request first
    await createDataSubjectRequest(organizationId, clientId, 'erasure', {
      reason,
      requested_by: user?.id,
    })

    // Delete all client data (cascade should handle most, but we'll be explicit)
    const tables = [
      'client_documents',
      'client_communications',
      'client_tag_assignments',
      'sessions',
      'therapies',
      'care_plans',
      'examinations',
      'medical_histories',
      'clients',
    ]

    for (const table of tables) {
      await supabase
        .from(table)
        .delete()
        .eq('client_id', clientId)
        .eq('organization_id', organizationId)
    }

    return { success: true }
  } catch (error) {
    console.error('Error deleting client data:', error)
    throw error
  }
}

/**
 * Record consent
 */
export const recordConsent = async (organizationId, clientId, consentType, consentStatus, consentText = null) => {
  try {
    const { data, error } = await supabase
      .from('consent_records')
      .insert({
        organization_id: organizationId,
        client_id: clientId,
        consent_type: consentType,
        consent_status: consentStatus,
        consent_method: 'explicit',
        consent_text: consentText,
        granted_at: consentStatus === 'granted' ? new Date().toISOString() : null,
        withdrawn_at: consentStatus === 'withdrawn' ? new Date().toISOString() : null,
        user_agent: navigator.userAgent,
      })
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error recording consent:', error)
    throw error
  }
}

/**
 * Get consent records for client
 */
export const getConsentRecords = async (organizationId, clientId) => {
  try {
    const { data, error } = await supabase
      .from('consent_records')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching consent records:', error)
    throw error
  }
}

